import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/marsheeApi";

const ELIGIBLE = new Set(["Active", "Incubating"]);

function PickOrAdd({ testid, currentId, options, placeholder, onPick, newFields, onCreate, disabled, disabledNote }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});

  if (disabled) {
    return <p className="mh-sans" data-testid={`${testid}-disabled`} style={{ fontSize: "0.9rem", color: "var(--mh-muted)", maxWidth: "44ch" }}>{disabledNote}</p>;
  }

  const submitNew = async (e) => {
    e.preventDefault();
    const ok = await onCreate(draft);
    if (ok) { setDraft({}); setAdding(false); }
  };

  return (
    <div className="space-y-3" style={{ maxWidth: "44ch" }}>
      {options.length > 0 && !adding && (
        <select
          className="mh-input"
          data-testid={`${testid}-select`}
          value={currentId || ""}
          onChange={(e) => e.target.value && onPick(e.target.value)}
        >
          <option value="">Choose…</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
        </select>
      )}

      {adding ? (
        <form onSubmit={submitNew} className="space-y-3" data-testid={`${testid}-new-form`}>
          {newFields.map((f) => (
            <div key={f.key}>
              <div className="mh-label">{f.label}</div>
              <input
                className="mh-input mt-1"
                data-testid={`${testid}-new-${f.key}`}
                value={draft[f.key] || ""}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                placeholder={f.placeholder || ""}
                required={f.required}
              />
            </div>
          ))}
          <div className="flex gap-3">
            <button type="submit" className="mh-btn mh-btn-primary" data-testid={`${testid}-new-save`} disabled={!draft.title?.trim()}>Save</button>
            <button type="button" className="mh-btn mh-btn-ghost" onClick={() => { setAdding(false); setDraft({}); }}>Cancel</button>
          </div>
        </form>
      ) : (
        <button type="button" className="mh-label" data-testid={`${testid}-add`} onClick={() => setAdding(true)}
          style={{ background: "none", cursor: "pointer", color: "var(--mh-action)" }}>
          {placeholder}
        </button>
      )}
    </div>
  );
}

export default function AlignmentEditor({ alignment, onChanged, onClose }) {
  const [seasons, setSeasons] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [flash, setFlash] = useState(null);

  const loadLists = useCallback(async () => {
    const [ss, st, oc, pr] = await Promise.all([api.seasons(), api.strategies(), api.outcomes(), api.projects()]);
    setSeasons(ss); setStrategies(st); setOutcomes(oc); setProjects(pr);
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  const refresh = async () => { await loadLists(); await onChanged(); };
  const run = async (fn, msg) => {
    setFlash(null);
    try { await fn(); await refresh(); setFlash(msg); return true; }
    catch (e) { setFlash(e.message); return false; }
  };

  const seasonId = alignment?.season?.id || null;
  const strategyId = alignment?.strategy?.id || null;
  const driver = alignment?.primary_driver || null;

  const seasonStrategies = strategies.filter((s) => s.season_id === seasonId);
  const strategyOutcomes = outcomes.filter((o) => o.strategy_id === strategyId);
  const eligibleProjects = projects.filter((p) => ELIGIBLE.has(p.status));

  return (
    <div className="mt-8 mh-fade" data-testid="alignment-editor" style={{ borderTop: "1px solid var(--mh-divider)", paddingTop: "1.75rem" }}>
      <div className="flex items-baseline justify-between mb-6">
        <span className="mh-label" style={{ color: "var(--mh-action)" }}>Adjusting your chapter</span>
        <button className="mh-label" data-testid="alignment-close" onClick={onClose} style={{ background: "none", cursor: "pointer", color: "var(--mh-muted)" }}>Done</button>
      </div>

      {flash && (
        <div className="mh-fade mb-6" data-testid="alignment-flash" style={{ borderLeft: "2px solid var(--mh-action)", paddingLeft: "0.9rem", color: "var(--mh-muted)", fontSize: "0.9rem" }}>{flash}</div>
      )}

      <div className="space-y-8">
        <section data-testid="edit-season">
          <div className="mh-label mb-2">The season you are living</div>
          {alignment?.season && <p className="mh-serif mb-3" style={{ fontSize: "1.15rem", color: "var(--mh-text)" }}>{alignment.season.title}</p>}
          <PickOrAdd
            testid="season"
            currentId={seasonId}
            options={seasons}
            placeholder={alignment?.season ? "Name a different season" : "Name this season"}
            onPick={(id) => run(() => api.setCurrentSeason(id), "Season updated.")}
            newFields={[{ key: "title", label: "Season", required: true, placeholder: "e.g. Rebuilding with intention" }, { key: "description", label: "In a sentence (optional)" }]}
            onCreate={(d) => run(() => api.createSeason({ title: d.title.trim(), description: d.description?.trim() || null, is_current: true }), "Season set.")}
          />
        </section>

        <section data-testid="edit-strategy">
          <div className="mh-label mb-2">How you are approaching it</div>
          {alignment?.strategy && <p className="mh-serif mb-3" style={{ fontSize: "1.15rem", color: "var(--mh-text)" }}>{alignment.strategy.title}</p>}
          <PickOrAdd
            testid="strategy"
            currentId={strategyId}
            options={seasonStrategies}
            placeholder={alignment?.strategy ? "Choose a different approach" : "Name your approach"}
            disabled={!seasonId}
            disabledNote="Name the season you are living first — an approach belongs to a season."
            onPick={(id) => run(() => api.setCurrentStrategy(id), "Approach updated.")}
            newFields={[{ key: "title", label: "Approach", required: true, placeholder: "e.g. Focus on retention" }, { key: "description", label: "In a sentence (optional)" }]}
            onCreate={(d) => run(() => api.createStrategy({ season_id: seasonId, title: d.title.trim(), description: d.description?.trim() || null, is_current: true }), "Approach set.")}
          />
        </section>

        <section data-testid="edit-outcome">
          <div className="mh-label mb-2">What matters most right now</div>
          {alignment?.primary_outcome && <p className="mh-serif mb-3" style={{ fontSize: "1.15rem", color: "var(--mh-text)" }}>{alignment.primary_outcome.title}</p>}
          <PickOrAdd
            testid="outcome"
            currentId={alignment?.primary_outcome?.id || null}
            options={strategyOutcomes}
            placeholder={alignment?.primary_outcome ? "Choose a different focus" : "Name what matters most"}
            disabled={!strategyId}
            disabledNote="Choose your approach first — the thing that matters most lives within it."
            onPick={(id) => run(() => api.setCurrentOutcome(id), "Focus updated.")}
            newFields={[{ key: "title", label: "The outcome", required: true, placeholder: "e.g. First ten happy customers" }, { key: "why_it_matters", label: "Why it matters (optional)" }]}
            onCreate={(d) => run(() => api.createOutcome({ strategy_id: strategyId, title: d.title.trim(), why_it_matters: d.why_it_matters?.trim() || null, is_current: true }), "Focus set.")}
          />
        </section>

        <section data-testid="edit-driver" style={{ borderTop: "1px solid var(--mh-divider)", paddingTop: "1.5rem" }}>
          <div className="mh-label mb-2">Primary Strategic Driver</div>
          {driver ? (
            <p className="mh-serif mb-3" data-testid="driver-current" style={{ fontSize: "1.15rem", color: "var(--mh-text)" }}>{driver.title}</p>
          ) : (
            <p className="mh-sans mb-3" style={{ fontSize: "0.9rem", color: "var(--mh-muted)" }}>No project currently carries the most weight.</p>
          )}
          {eligibleProjects.length === 0 ? (
            <p className="mh-sans" style={{ fontSize: "0.9rem", color: "var(--mh-muted)", maxWidth: "44ch" }}>
              Only an Active or Incubating project can carry this. None are eligible yet.
            </p>
          ) : (
            <select
              className="mh-input"
              data-testid="driver-select"
              style={{ maxWidth: "44ch" }}
              value={driver?.id || ""}
              onChange={(e) => run(() => api.setPrimaryDriver(e.target.value), "Strategic driver updated.")}
            >
              <option value="">Choose the project carrying the most weight…</option>
              {eligibleProjects.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.status}</option>)}
            </select>
          )}
          {driver && (
            <button className="mh-btn mh-btn-ghost mt-3" data-testid="driver-clear" onClick={() => run(() => api.unsetPrimaryDriver(), "Strategic driver cleared.")}>
              Clear the driver
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
